<template>
    <div class="container profile-container">
        <h2 class="my-3">{{ $t('profile.title') }}</h2>

        <div v-if="currentUser" class="card shadow border-0">
            <ul class="list-group list-group-flush">
                <li class="list-group-item">
                    <div class="text-body-secondary extra-small text-uppercase">{{ $t('profile.name') }}</div>
                    <div class="text-break">{{ currentUser.name }}</div>
                </li>
                <li v-if="currentUser.username" class="list-group-item">
                    <div class="text-body-secondary extra-small text-uppercase">{{ $t('profile.username') }}</div>
                    <div class="text-break">{{ currentUser.username }}</div>
                </li>
                <li v-if="jobTitle" class="list-group-item">
                    <div class="text-body-secondary extra-small text-uppercase">{{ $t('profile.jobTitle') }}</div>
                    <div class="text-break">{{ jobTitle }}</div>
                </li>
                <li v-if="email" class="list-group-item">
                    <div class="text-body-secondary extra-small text-uppercase">{{ $t('profile.email') }}</div>
                    <div class="text-break">{{ email }}</div>
                </li>
                <li v-if="phone" class="list-group-item">
                    <div class="text-body-secondary extra-small text-uppercase">{{ $t('profile.phone') }}</div>
                    <div class="text-break">{{ phone }}</div>
                </li>
            </ul>
        </div>

        <div class="mt-4">
            <h6 class="mb-3">{{ $t('profile.appearance') }}</h6>
            <div class="card shadow border-0">
                <div class="card-body">
                    <!-- Wraps only where the three segments cannot share the row
                         with the label, which is the longest locale on a narrow phone. -->
                    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>{{ $t('profile.theme') }}</span>
                        <div class="btn-group btn-group-sm theme-group" role="group"
                            :aria-label="$t('profile.theme')">
                            <template v-for="option in themeOptions" :key="option.value">
                                <input type="radio" class="btn-check" name="themePreference"
                                    :id="`theme-${option.value}`" :value="option.value"
                                    :checked="themePreference === option.value"
                                    @change="onSelectTheme(option.value)">
                                <label class="btn btn-outline-primary" :for="`theme-${option.value}`">
                                    {{ $t(option.labelKey) }}
                                </label>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="canChangePassword" class="mt-4">
            <h6 class="mb-3">{{ $t('password.changeTitle') }}</h6>
            <div class="card shadow border-0">
                <div class="card-body">
                    <form v-if="editingPassword" @submit.prevent="savePassword">
                        <label for="current-password" class="form-label text-body-secondary extra-small text-uppercase mb-1">{{ $t('password.current') }}</label>
                        <input v-model="passwordForm.current" type="password" class="form-control form-control-sm mb-2"
                            id="current-password" autocomplete="current-password" required>
                        <label for="new-password" class="form-label text-body-secondary extra-small text-uppercase mb-1">{{ $t('password.new') }}</label>
                        <input v-model="passwordForm.next" type="password" class="form-control form-control-sm mb-2"
                            id="new-password" autocomplete="new-password" :minlength="passwordMinLength" required>
                        <label for="confirm-password" class="form-label text-body-secondary extra-small text-uppercase mb-1">{{ $t('password.confirm') }}</label>
                        <input v-model="passwordForm.confirm" type="password" class="form-control form-control-sm"
                            id="confirm-password" autocomplete="new-password" :minlength="passwordMinLength" required>
                        <div class="d-flex justify-content-end mt-2">
                            <button type="button" class="btn btn-sm btn-outline-primary me-2" :disabled="savingPassword"
                                @click="cancelEditPassword">{{ $t('common.cancel') }}</button>
                            <button type="submit" class="btn btn-sm btn-primary" :disabled="savingPassword">{{ $t('common.save') }}</button>
                        </div>
                    </form>
                    <div v-else class="d-flex justify-content-end">
                        <button class="btn btn-sm btn-primary" @click="editingPassword = true">{{ $t('password.change') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-4">
            <h6 class="mb-3">{{ $t('profile.signature') }}</h6>
            <div class="card shadow border-0">
                <div class="card-body">
                    <template v-if="editingSignature">
                        <vue-signature-pad class="bg-white signature-pad" ref="signaturePad"></vue-signature-pad>
                        <div class="d-flex justify-content-between align-items-center mt-2">
                            <button class="btn-icon bg-danger-subtle text-danger-emphasis" type="button" :title="$t('modal.clear')"
                                @click="clearSignature"><i class="fa-regular fa-trash-can"></i></button>
                            <div>
                                <button class="btn btn-sm btn-outline-primary me-2" :disabled="savingSignature"
                                    @click="cancelEditSignature">{{ $t('common.cancel') }}</button>
                                <button class="btn btn-sm btn-primary" :disabled="savingSignature"
                                    @click="saveUserSignature">{{ $t('common.save') }}</button>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <img v-if="signatureUrl" :src="signatureUrl" alt="signature" class="signature-preview">
                        <div v-else class="text-body-secondary small fst-italic">{{ $t('profile.noSignature') }}</div>
                        <div class="d-flex justify-content-end mt-2">
                            <button class="btn btn-sm btn-primary" @click="startEditSignature">{{ $t('common.edit') }}</button>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <div v-if="qualifications.length > 0" class="mt-4">
            <h6 class="mb-3">{{ $t('profile.qualifications') }}</h6>
            <div class="card shadow border-0">
                <ul class="list-group list-group-flush">
                    <li v-for="qualification in qualifications" :key="qualification.activity_profile_id"
                        class="list-group-item">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="fw-medium me-2 text-break">{{ qualification.name }}</div>
                            <button v-if="editingId !== qualification.activity_profile_id"
                                class="btn-icon bg-primary-subtle text-primary-emphasis flex-shrink-0" :title="$t('common.edit')"
                                @click="startEdit(qualification)"><i class="fa-regular fa-pen-to-square"></i></button>
                        </div>

                        <div v-if="editingId === qualification.activity_profile_id" class="mt-2">
                            <label class="form-label text-body-secondary extra-small text-uppercase mb-1">{{ $t('profile.certificate') }}</label>
                            <input v-model="editValue" type="text" class="form-control form-control-sm"
                                :placeholder="$t('profile.certificate')">
                            <div class="d-flex justify-content-end mt-2">
                                <button class="btn btn-sm btn-outline-primary me-2" :disabled="saving" @click="cancelEdit">{{ $t('common.cancel') }}</button>
                                <button class="btn btn-sm btn-primary" :disabled="saving"
                                    @click="saveCertificate(qualification)">{{ $t('common.save') }}</button>
                            </div>
                        </div>
                        <div v-else class="text-body-secondary small mt-1 text-break">
                            <span v-if="qualification.certificate">{{ qualification.certificate }}</span>
                            <span v-else class="fst-italic">{{ $t('profile.noCertificate') }}</span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <ProfileTemplateList
            class="mt-4"
            :templates="textTemplates"
            :type-id="TEMPLATE_TYPE.text"
            :max="maxTemplates"
            :title="$t('profile.textTemplates')"
            :empty-text="$t('profile.noTextTemplates')"
            :add-label="$t('profile.addTextTemplate')"
            :limit-text="$t('profile.templateLimitReached', { count: maxTemplates })"
            @update:templates="onTextTemplatesChanged" />

        <ProfileTemplateList
            class="mt-4"
            :templates="emailTemplates"
            :type-id="TEMPLATE_TYPE.email"
            :max="maxTemplates"
            :title="$t('profile.emailTemplates')"
            :empty-text="$t('profile.noEmailTemplates')"
            :add-label="$t('profile.addEmailTemplate')"
            :limit-text="$t('profile.emailTemplateLimitReached', { count: maxTemplates })"
            @update:templates="onEmailTemplatesChanged" />

    </div>
</template>

<script>
import axios from '@/services/axios';
import { VueSignaturePad } from 'vue-signature-pad';
import ProfileTemplateList from '@/components/ProfileTemplateList.vue';
import { setTextTemplates, setEmailTemplates } from '@/composables/useTextTemplates';
import { themePreference as storedTheme, setThemePreference, THEME_PREFERENCES } from '@/composables/useTheme';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '@/utils/password';

const EMPTY_PASSWORD_FORM = { current: '', next: '', confirm: '' };

// Mirrors USER_TEXT_TEMPLATES_MAX in php/constants.php, which is the source of
// truth — the API returns 409 past the cap regardless of what the client shows.
// Counted per type since the API split them, so each list gets its own 20.
const USER_TEXT_TEMPLATES_MAX = 20;

// USER_TEXT_TEMPLATE_TYPE in php/constants.php. Append-only: the values are
// persisted on every row.
const TEMPLATE_TYPE = { text: 1, email: 2 };

// Segments of the theme picker, in the order they are shown.
const THEME_OPTIONS = THEME_PREFERENCES.map((value) => ({
    value,
    labelKey: `profile.theme${value.charAt(0).toUpperCase()}${value.slice(1)}`,
}));

export default {
    name: 'ProfileView',
    components: {
        VueSignaturePad,
        ProfileTemplateList,
    },
    data() {
        return {
            profile: null,
            qualifications: [],
            editingId: null,
            editValue: '',
            saving: false,
            signatureUrl: null,
            editingSignature: false,
            savingSignature: false,
            textTemplates: [],
            emailTemplates: [],
            maxTemplates: USER_TEXT_TEMPLATES_MAX,
            TEMPLATE_TYPE,
            themeOptions: THEME_OPTIONS,
            editingPassword: false,
            savingPassword: false,
            passwordForm: { ...EMPTY_PASSWORD_FORM },
            passwordMinLength: PASSWORD_MIN_LENGTH,
        };
    },
    computed: {
        // Display preference only, stored per device — see composables/useTheme.js.
        themePreference() {
            return storedTheme.value;
        },
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        },
        // Read-only details: the profile endpoint is the source of truth, the
        // stored login user is only a fallback until that request resolves.
        jobTitle() {
            return this.profile?.job_title || '';
        },
        email() {
            return this.profile?.email || this.currentUser?.email || '';
        },
        phone() {
            return this.profile?.phone || this.currentUser?.phone || '';
        },
        // Only accounts allowed to log in with a password, whichever method
        // this session used. Hidden until the profile request resolves.
        canChangePassword() {
            return this.profile?.password_login === true;
        }
    },
    methods: {
        onSelectTheme(value) {
            setThemePreference(value);
        },
        async fetchProfile() {
            try {
                const response = await axios.get('/users/profile');
                this.profile = response.data.data || null;
                this.qualifications = response.data.data.qualifications || [];
                this.textTemplates = response.data.data.text_templates || [];
                this.emailTemplates = response.data.data.email_templates || [];
                // The profile response already carries them, so keep the shared
                // cache in step rather than making the composable fetch again.
                setTextTemplates(this.textTemplates);
                setEmailTemplates(this.emailTemplates);
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
        },
        async fetchSignature() {
            if (!this.currentUser?.id) return;
            try {
                const response = await axios.get(`/users/${this.currentUser.id}/signature`);
                this.signatureUrl = response.data?.data || null;
            } catch (error) {
                console.error('Error fetching signature:', error);
            }
        },
        startEdit(qualification) {
            this.editingId = qualification.activity_profile_id;
            this.editValue = qualification.certificate || '';
        },
        cancelEdit() {
            this.editingId = null;
            this.editValue = '';
        },
        async saveCertificate(qualification) {
            this.saving = true;
            try {
                await axios.patch(`/users/profile/qualifications/${qualification.activity_profile_id}`, {
                    certificate: this.editValue
                });
                qualification.certificate = this.editValue;
                this.cancelEdit();
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saved'), 'success');
            } catch (error) {
                console.error('Error saving certificate:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saveFailed'), 'danger');
            } finally {
                this.saving = false;
            }
        },
        // Write-through, so an edit here shows up in the task-detail pickers
        // without a refetch or a re-login.
        onTextTemplatesChanged(list) {
            this.textTemplates = list;
            setTextTemplates(list);
        },
        onEmailTemplatesChanged(list) {
            this.emailTemplates = list;
            setEmailTemplates(list);
        },
        cancelEditPassword() {
            this.editingPassword = false;
            this.passwordForm = { ...EMPTY_PASSWORD_FORM };
        },
        async savePassword() {
            const { current, next, confirm } = this.passwordForm;
            const problem = passwordProblem(next, confirm);
            if (problem) {
                this.$root.$refs.globalAlert?.showAlert(this.$t(problem, { min: PASSWORD_MIN_LENGTH }), 'danger');
                return;
            }
            this.savingPassword = true;
            try {
                await axios.post('/users/profile/password', {
                    current_password: current,
                    new_password: next,
                    confirm_password: confirm
                });
                this.cancelEditPassword();
                this.$root.$refs.globalAlert?.showAlert(this.$t('password.changed'), 'success');
            } catch (error) {
                const status = error.response?.status;
                let key = 'common.saveFailed';
                if (status === 422) {
                    // One status for both causes; the message tells them apart.
                    key = error.response.data?.error?.message === 'Invalid current password'
                        ? 'password.wrongCurrent'
                        : 'password.invalidNew';
                } else if (status === 403) {
                    key = 'password.notAllowed';
                }
                console.error('Error changing password:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t(key, { min: PASSWORD_MIN_LENGTH }), 'danger');
            } finally {
                this.savingPassword = false;
            }
        },
        startEditSignature() {
            this.editingSignature = true;
            this.$nextTick(() => {
                if (this.signatureUrl && this.$refs.signaturePad) {
                    this.$refs.signaturePad.fromDataURL(this.signatureUrl);
                }
            });
        },
        cancelEditSignature() {
            this.editingSignature = false;
        },
        clearSignature() {
            this.$refs.signaturePad.clearSignature();
        },
        // Redraw a captured signature onto a fixed-size canvas so every stored
        // signature has consistent dimensions regardless of screen width / device pixel ratio.
        normalizeSignature(result) {
            const TARGET_WIDTH = 450;
            const TARGET_HEIGHT = 141; // keeps the ~3.2:1 frame (matches the display aspect ratio)
            return new Promise((resolve) => {
                if (!result || result.isEmpty || !result.data) {
                    resolve(result);
                    return;
                }
                const canvas = document.createElement('canvas');
                canvas.width = TARGET_WIDTH;
                canvas.height = TARGET_HEIGHT;
                const ctx = canvas.getContext('2d');
                const image = new Image();
                image.onload = () => {
                    const scale = Math.min(TARGET_WIDTH / image.width, TARGET_HEIGHT / image.height);
                    const drawWidth = image.width * scale;
                    const drawHeight = image.height * scale;
                    const dx = (TARGET_WIDTH - drawWidth) / 2;
                    const dy = (TARGET_HEIGHT - drawHeight) / 2;
                    ctx.drawImage(image, dx, dy, drawWidth, drawHeight);
                    resolve({ ...result, data: canvas.toDataURL('image/png') });
                };
                image.onerror = () => resolve(result);
                image.src = result.data;
            });
        },
        async saveUserSignature() {
            const result = this.$refs.signaturePad.saveSignature();
            if (!result || result.isEmpty) {
                this.$root.$refs.globalAlert?.showAlert(this.$t('profile.emptySignature'), 'danger');
                return;
            }
            this.savingSignature = true;
            try {
                const normalized = await this.normalizeSignature(result);
                await axios.put('/users/profile/signature', {
                    signature: normalized.data
                });
                this.signatureUrl = normalized.data;
                this.editingSignature = false;
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saved'), 'success');
            } catch (error) {
                console.error('Error saving signature:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.saveFailed'), 'danger');
            } finally {
                this.savingSignature = false;
            }
        }
    },
    mounted() {
        this.fetchProfile();
        this.fetchSignature();
    }
};
</script>

<style scoped>
/* Keep the three segments narrow enough to share the row with the label, even
   in Estonian where "Automaatne" is the longest of them. */
.theme-group {
    --bs-btn-padding-x: 0.75rem;
}

/* Clears the fixed GlobalAlert toast (bottom: 1rem, ~3.5rem tall) so it never
   covers the Add button at the end of the page. */
.profile-container {
    margin-bottom: 5rem;
}

.signature-pad {
    width: 100%;
    max-width: 600px;
    height: auto !important;
    aspect-ratio: 16 / 5;
    border: 1px solid var(--bs-border-color);
}

.signature-preview {
    width: 100%;
    max-width: 600px;
    aspect-ratio: 16 / 5;
    object-fit: contain;
    border: 1px solid var(--bs-border-color);
    /* The stored signature is black strokes on transparency, so the backdrop
       has to stay light or it vanishes in dark mode. */
    background-color: var(--bs-white);
}

/* Dark mode shows the pad inverted — black backdrop, white strokes — purely as a
   CSS filter. Filters affect rendering only, never the canvas bitmap, so
   saveSignature()/toDataURL() still return the same black-on-transparent image
   the API and the server-rendered PDF expect. Anything that changed the pen
   colour instead would change what gets submitted. The filter goes on the
   canvas, not the wrapper, so the wrapper's themed border is not inverted too. */
[data-bs-theme='dark'] .signature-pad {
    background-color: #000 !important;
}

/* :deep() is required — the <canvas> belongs to vue-signature-pad's own
   template, so it never receives this component's data-v attribute and a plain
   descendant selector compiles to canvas[data-v-x], which matches nothing. */
[data-bs-theme='dark'] .signature-pad :deep(canvas) {
    filter: invert(1);
}

[data-bs-theme='dark'] .signature-preview {
    filter: invert(1);
    border-color: #8A8A8A;
}
</style>
